import { forwardRef, useCallback } from 'react'

import { Avatar, Box, Button } from '@factorialco/deprecated-design-system'
import { SearchEmployeesForMentionsDocument } from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import { MentionedAccess } from 'modules/core/components/TextEditor/plugins/DebouncedMentionsPlugin'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { Return } from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useCommunitiesClapsFeature } from 'modules/posts/hooks/useCommunitiesClapsFeature'

interface Props {
  saving: boolean
  onSubmit: () => Promise<void | Return<void>>
  comment: string | null
  setComment: (value: string) => void
  setUsersIds: (value: string[]) => void
}

const CommentInput = forwardRef<EditorHandle, Props>(function CommentInput(
  { saving, onSubmit, setComment, comment, setUsersIds },
  ref
) {
  const {
    access: { fullName, avatar },
    company: { id: companyId },
  } = useCurrentSession()
  const hasClaps = useCommunitiesClapsFeature()

  const [searchEmployees] = useLazyQuery(SearchEmployeesForMentionsDocument)

  const handleMentionSearch = useCallback(
    async (queryString: string): Promise<MentionedAccess[]> => {
      if (!queryString) {
        return []
      }

      try {
        const result = await searchEmployees({
          variables: { companyId, search: queryString },
        })

        const nodes = result.data?.employees?.employeeNamesConnection?.nodes
        if (!nodes) return []

        return nodes.map((employee) => ({
          id: employee.access.id,
          employeeId: employee.id,
          fullName: employee.fullName ?? '',
          avatar: employee.avatar?.url ?? '',
        }))
      } catch {
        return []
      }
    },
    [companyId, searchEmployees]
  )

  return (
    <Box width="full" gap="s24" flexDirection="row" paddingBottom="s24">
      <Avatar url={buildUrl(avatar)} label={fullName} size="s48" />
      <Box
        paddingX="s24"
        paddingY="s16"
        background="grey200"
        borderRadius={{
          all: 'abs008',
        }}
        width="full"
      >
        {hasClaps ? (
          <TextEditor
            ref={ref}
            initialEditorState={{ data: comment }}
            onChange={(data) => {
              if (data && typeof data === 'object') {
                setComment(data.value)
                setUsersIds(data?.ids)
              }
            }}
            placeholder={i18n.t('posts.comments.placeholder')}
            height="s96"
            isRichText={false}
            hasDebouncedMentions
            onMentionQueryStringChanged={handleMentionSearch}
          />
        ) : (
          <TextArea
            placeholder={i18n.t('posts.comments.placeholder')}
            value={comment ?? ''}
            onChange={(value) => setComment(value)}
          />
        )}
        <Box paddingTop="s16" flexDirection="rowReverse">
          <Button type="secondary" disabled={saving || comment === ''} onClick={onSubmit}>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
})

export default CommentInput
